import * as Sentry from "@sentry/react";
import { readSentryConfig, type SentryConfig } from "./config";

const config: SentryConfig = readSentryConfig();
let initialized = false;

function devWarn(context: string, error: unknown) {
  if (import.meta.env.DEV) {
    console.warn(`[observability] ${context}`, error);
  }
}

/** No-op when VITE_SENTRY_ENABLED/VITE_SENTRY_DSN are unset -- see docs/OBSERVABILITY.md#local-development. */
export function initSentry(): void {
  if (!config.enabled || !config.dsn || initialized) return;
  try {
    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      release: config.release ?? undefined,
      tracesSampleRate: config.tracesSampleRate,
      // Never send cookies/IP/user data by default. AI content (prompts, reflections, goals)
      // never reaches the browser bundle's Sentry calls in the first place.
      sendDefaultPii: false,
      integrations: config.tracesSampleRate > 0 ? [Sentry.browserTracingIntegration()] : [],
    });
    initialized = true;
  } catch (error) {
    devWarn("Sentry init failed", error);
  }
}

/** Reports a caught error. Safe to call unconditionally -- a no-op when Sentry is disabled. */
export function captureException(error: unknown, extra?: Record<string, unknown>): void {
  if (!config.enabled) return;
  try {
    Sentry.captureException(error, extra ? { extra } : undefined);
  } catch (sentryError) {
    devWarn("captureException failed", sentryError);
  }
}

/** Tags the active Sentry scope with the request's correlation id so a Sentry event can be
 * traced back to the same request in Grafana/Langfuse/backend logs. See docs/OBSERVABILITY.md#correlation. */
export function setRequestIdTag(requestId: string | null | undefined): void {
  if (!config.enabled || !requestId) return;
  try {
    Sentry.setTag("request_id", requestId);
  } catch (error) {
    devWarn("setRequestIdTag failed", error);
  }
}

export function getSentryConfig(): SentryConfig {
  return config;
}
